<template>
  <Page :title="title" :top-image="topImage">
    <v-row no-gutters class="wider mt-10 mt-md-16 pt-md-4">
      <v-col no-gutters>
        <div :class="[sectionNumberFont, xPadding]">01</div>
        <div :class="[sectionHeaderFont, xPadding]" class="mt-2 mt-md-5">General</div>
        <v-expansion-panels class="reflective-card mt-5 mt-md-10" :class="padding" flat>
          <v-expansion-panel>
            <v-expansion-panel-header :class=questionTitleFont class="faq-section-question py-5 py-7" color="black">
              <template v-slot:actions>
                <v-icon color="white">$expand</v-icon>
              </template>
              Which networks are currently supported by the bridge?
            </v-expansion-panel-header>
            <v-expansion-panel-content :class=questionAnswerFont class="faq-section-answer pb-5 pb-md-7" color="black">
              <div>
                <p>The Timeworx.io Bridge currently supports cross-chain TIX transfers between the following networks: Injective, MultiversX and BSC (Binance Smart Chain).</p>
                <p>All routes between these networks are fully available. You can bridge your TIX tokens freely between any of these three chains.</p>
              </div>
            </v-expansion-panel-content>
            <hr />
          </v-expansion-panel>

          <v-expansion-panel>
            <v-expansion-panel-header :class=questionTitleFont class="faq-section-question py-5 py-7" color="black">
              <template v-slot:actions>
                <v-icon color="white">$expand</v-icon>
              </template>
              Can I use the bridge on my mobile phone?
            </v-expansion-panel-header>
            <v-expansion-panel-content :class=questionAnswerFont class="faq-section-answer pb-5 pb-md-7" color="black">
              <div>
                No. Currently, the Timeworx Bridge is available as a <span style="font-weight: 700">Desktop-Web</span> version only. For the safety of your assets and the stability of the connection, please access the bridge via a desktop browser.
              </div>
            </v-expansion-panel-content>
            <hr />
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>

    <v-row no-gutters class="wider mt-10 mt-md-16 pt-md-4">
      <v-col no-gutters>
        <div :class="[sectionNumberFont, xPadding]">02</div>
        <div :class="[sectionHeaderFont, xPadding]" class="mt-2 mt-md-5">Fees & Transaction Limits</div>
        <v-expansion-panels class="reflective-card mt-5 mt-md-10" :class="padding" flat>
          <v-expansion-panel>
            <v-expansion-panel-header :class=questionTitleFont class="faq-section-question py-5 py-7" color="black">
              <template v-slot:actions>
                <v-icon color="white">$expand</v-icon>
              </template>
              Are there fees for bridging TIX tokens?
            </v-expansion-panel-header>
            <v-expansion-panel-content :class=questionAnswerFont class="faq-section-answer pb-5 pb-md-7" color="black">
              <div>
                <p>Timeworx does <span style="font-weight: 700">not charge a service fee</span> for using the bridge. However, you are required to pay <span style="font-weight: 700">gas fees</span> on both the sending network (to initiate the transfer) and the receiving network (to claim/receive the tokens).</p>
                <p class="px-3"><span style="font-weight: 700">Note:</span> Please ensure you have enough native tokens (e.g., INJ, EGLD, or BNB) on the respective networks to cover these gas costs.</p>
              </div>
            </v-expansion-panel-content>
            <hr />
          </v-expansion-panel>

          <v-expansion-panel>
            <v-expansion-panel-header :class=questionTitleFont class="faq-section-question py-5 py-7" color="black">
              <template v-slot:actions>
                <v-icon color="white">$expand</v-icon>
              </template>
              Is there a limit to how much TIX I can transfer?
            </v-expansion-panel-header>
            <v-expansion-panel-content :class=questionAnswerFont class="faq-section-answer pb-5 pb-md-7" color="black">
              <div>
                Yes, there is currently a limit of <span style="font-weight: 700">per transaction</span>.
                <ul class="mt-2">
                  <li><span style="font-weight: 700">Per Day:</span> There is no limit on the number of transactions you can perform daily.</li>
                  <li><span style="font-weight: 700">Why the limit?</span> This cap is in place to allow us to monitor the proper functioning of the bridge and detect potential anomalies during the launch phase.</li>
                  <li><span style="font-weight: 700">Future Updates:</span> We plan to relax and increase these limits over time as the system matures.</li>
                </ul>
              </div>
            </v-expansion-panel-content>
            <hr />
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>

    <v-row no-gutters class="wider mt-10 mt-md-16 pt-md-4">
      <v-col no-gutters>
        <div :class="[sectionNumberFont, xPadding]">03</div>
        <div :class="[sectionHeaderFont, xPadding]" class="mt-2 mt-md-5">Troubleshooting</div>
        <v-expansion-panels class="reflective-card mt-5 mt-md-10" :class="padding" flat>
          <v-expansion-panel>
            <v-expansion-panel-header :class=questionTitleFont class="faq-section-question py-5 py-7" color="black">
              <template v-slot:actions>
                <v-icon color="white">$expand</v-icon>
              </template>
              How does the bridging process work?
            </v-expansion-panel-header>
            <v-expansion-panel-content :class=questionAnswerFont class="faq-section-answer pb-5 pb-md-7" color="black">
              <div>
                To successfully bridge your assets, you must sign <span style="font-weight: 700">two separate transactions</span>:
                <ol class="mt-2">
                  <li><span style="font-weight: 700">Sending:</span> You sign a transaction on the source network to lock/send your TIX.</li>
                  <li><span style="font-weight: 700">Receiving: </span> You sign a second transaction on the destination network to claim your TIX.</li>
                </ol>
              </div>
            </v-expansion-panel-content>
            <hr />
          </v-expansion-panel>

          <v-expansion-panel>
            <v-expansion-panel-header :class=questionTitleFont class="faq-section-question py-5 py-7" color="black">
              <template v-slot:actions>
                <v-icon color="white">$expand</v-icon>
              </template>
              Can I cancel a transaction once it has started?
            </v-expansion-panel-header>
            <v-expansion-panel-content :class=questionAnswerFont class="faq-section-answer pb-5 pb-md-7" color="black">
              <div>
                No. Once you have signed the initial sending transaction, the transfer <span style="font-weight: 700">cannot be cancelled</span>. The assets are already in transit, and you must proceed to claim them on the destination network.
              </div>
            </v-expansion-panel-content>
            <hr />
          </v-expansion-panel>

          <v-expansion-panel>
            <v-expansion-panel-header :class=questionTitleFont class="faq-section-question py-5 py-7" color="black">
              <template v-slot:actions>
                <v-icon color="white">$expand</v-icon>
              </template>
              What happens if I sign the sending transaction but fail to sign the receiving one?
            </v-expansion-panel-header>
            <v-expansion-panel-content :class=questionAnswerFont class="faq-section-answer pb-5 pb-md-7" color="black">
              <div>
                If you close the window, lose internet connection, or simply fail to sign the second transaction, your funds are safe but "pending." The next time you connect to the bridge, the system will <span style="font-weight: 700">force you to resume the transaction</span>. You must complete the receiving signature to finalize the transfer and unlock your bridge usage.
              </div>
            </v-expansion-panel-content>
            <hr />
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>
  </Page>
</template>

<script>
import Page from "../Page.vue";
import { getFirestore, collection, getDocs, orderBy } from "firebase/firestore";

export default {
  name: "BridgeFAQ",
  metaInfo: {
    title: "Bridge FAQ"
  },
  components: { Page },
  props: [
    "title",
    "topImage",
  ],
  data() {
    return {
      sections: [],
    };
  },
  async mounted() {
    const querySnapshot = await getDocs(collection(getFirestore(), "faq"));
    const loadedSections = []
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      loadedSections.push({
        title: data.title,
        order: data.order,
        questions: data.questions.map((question) => {
          return {
            title: question.title,
            answer: question.answer,
          }
        }),
      });
    });
    this.sections = loadedSections.sort((a, b) => a.order - b.order);
  },
  computed: {
    sectionNumberFont() {
      switch (this.$vuetify.breakpoint.name) {
        case "xl":
        case "lg":
        case "md":
          return "faq-section-header poppins-normal-white-32px";
        case "sm":
        case "xs":
          return "faq-section-header poppins-normal-white-16px";
      }
    },
    sectionHeaderFont() {
      switch (this.$vuetify.breakpoint.name) {
        case "xl":
        case "lg":
        case "md":
          return "faq-section-header poppins-bold-white-64px";
        case "sm":
        case "xs":
          return "faq-section-header poppins-bold-white-32px";
      }
    },
    questionTitleFont() {
      switch (this.$vuetify.breakpoint.name) {
        case "xl":
        case "lg":
        case "md":
          return "poppins-normal-white-22px";
        case "sm":
        case "xs":
          return "poppins-normal-white-18px";
      }
    },
    questionAnswerFont() {
      switch (this.$vuetify.breakpoint.name) {
        case "xl":
        case "lg":
        case "md":
          return "poppins-normal-white-18px";
        case "sm":
        case "xs":
          return "poppins-normal-white-16px";
      }
    },
    padding() {
      switch (this.$vuetify.breakpoint.name) {
        case "xl":
        case "lg":
        case "md":
          return "padding-desktop";
        case "sm":
        case "xs":
          return "padding-mobile";
      }
    },
    xPadding() {
      switch (this.$vuetify.breakpoint.name) {
        case "xl":
        case "lg":
        case "md":
          return "x-padding-desktop";
        case "sm":
        case "xs":
          return "x-padding-mobile";
      }
    },
  },
};

Number.prototype.pad = function(size) {
    var s = String(this);
    while (s.length < (size || 2)) {s = "0" + s;}
    return s;
}
</script>

<style scoped>

.wider {
  width: 100%;
}

.faq-section-question {
  padding-left: 0px;
  padding-right: 0px;
  font-weight: 500;
  border: 0;
}

.faq-section-answer {
  color: #C9C4D0;
  border: 0;
}

* hr {
  display: block;
  height: 1px;
  border: 0;
  border-top: 1px solid #CBBEFF;
  padding: 0;
}

.padding-desktop {
  padding: 80px 160px;
}

.padding-mobile {
  padding: 20px;
}

.x-padding-desktop {
  padding: 0px 160px;
}

.x-padding-mobile {
  padding: 0px;
}

.v-expansion-panels > * {
  border-radius: 0 !important;
}

.v-expansion-panels:not(.v-expansion-panels--accordion):not(.v-expansion-panels--tile) > .v-expansion-panel--active {
  border-radius: 0 !important;
}

.v-expansion-panels:not(.v-expansion-panels--accordion):not(.v-expansion-panels--tile) > .v-expansion-panel--next-active {
  border-radius: 0 !important;
}

.v-expansion-panel--active>>>.v-expansion-panel-header {
  color: #957DFB;
}

.v-expansion-panel-content>>>.v-expansion-panel-content__wrap {
  padding: 0 !important;
}

.v-expansion-panel-content>>>a {
  color: #C9C4D0;
}

ul {
  list-style-type: disc;
}

ol {
  list-style: decimal;
}
</style>
